import type { ReactiveController, ReactiveControllerHost } from "lit";
import {
  createActor,
  type Actor,
  type ActorOptions,
  type AnyActorLogic,
  type EventFrom,
  type SnapshotFrom,
  type Subscription,
} from "xstate";
import { getActorInspectionObserver } from "./inspection";

export interface LitActorHost extends ReactiveControllerHost {
  requestUpdate(name?: PropertyKey, oldValue?: unknown): void;
}

/**
 * The intentionally small bridge between XState and Lit.
 *
 * It does not mirror actor context into decorated component fields. The actor
 * owns workflow state; Lit reads one typed snapshot and sends semantic events.
 */
export class LitActorController<
  TLogic extends AnyActorLogic,
> implements ReactiveController {
  readonly actor: Actor<TLogic>;

  private subscription: Subscription | undefined;
  private previousSnapshot: SnapshotFrom<TLogic>;

  constructor(
    private readonly host: LitActorHost,
    logic: TLogic,
    options: ActorOptions<TLogic>,
    private readonly changedProperties?: (
      previous: SnapshotFrom<TLogic>,
      next: SnapshotFrom<TLogic>,
    ) => readonly (readonly [PropertyKey, unknown])[],
  ) {
    const inspect = options.inspect ?? getActorInspectionObserver();
    this.actor = createActor(
      logic,
      inspect ? { ...options, inspect } : options,
    );
    this.previousSnapshot = this.actor.getSnapshot();
    this.host.addController(this);
    this.subscribe();
    this.actor.start();
  }

  get snapshot(): SnapshotFrom<TLogic> {
    return this.actor.getSnapshot();
  }

  send(event: EventFrom<TLogic>): void {
    this.actor.send(event);
  }

  hostConnected(): void {
    this.subscribe();
    this.host.requestUpdate();
  }

  hostDisconnected(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.actor.stop();
  }

  private subscribe(): void {
    if (this.subscription) return;
    this.subscription = this.actor.subscribe((snapshot) => {
      const previous = this.previousSnapshot;
      this.previousSnapshot = snapshot;
      for (const [key, oldValue] of this.changedProperties?.(
        previous,
        snapshot,
      ) ?? []) {
        this.host.requestUpdate(key, oldValue);
      }
      this.host.requestUpdate();
    });
  }
}
